import { DatePicker, InputLabel, TimePicker } from '@components/Common';
import React, { Component } from 'react';
import { WithTranslation, withTranslation } from 'react-i18next';
import styled from 'styled-components';
import { parseISO, isValid, getYear, getMonth, getDate, getHours, getMinutes } from 'date-fns';
import { toZonedTime, fromZonedTime } from 'date-fns-tz';

const DateTimeContainer = styled.div`
  display: flex;
  flex-direction: row;
  flex-flow: row wrap;

  .react-datepicker__input-container,
  .react-datepicker-wrapper {
    display: block;
  }

  .react-datepicker__close-icon {
    top: 22%;
  }

  .react-datepicker__close-icon:after {
    font-size: 16px;
    color: black;
    background: none;
  }

  > :nth-child(1) {
    flex: 1 100%;
    margin-bottom: 0px;
  }
  > :nth-child(2) {
    flex: 1;
    margin-right: 5px;
  }
  > :nth-child(3) {
    flex: 1;
    margin-left: 5px;
  }
`;

interface ComponentProps {
  title: string;
  name: string;
  dateTime?: string;
  hasTime: boolean;
  showTime: boolean;
  isDisabled?: boolean;
  isClearable?: boolean;
  changed: (value: string, hasTime: boolean) => void;
  datePlaceholder?: string;
}

type Props = ComponentProps & WithTranslation;

// Get the local timezone
const localTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

class DateTimePicker extends Component<Props, any> {
  constructor(props) {
    super(props);
  }

  public render() {
    const { t, title, name, isDisabled, isClearable, datePlaceholder } = this.props;
    const dateAndTime = this.getDateAndTimeFromProps();

    const timePicker = (
      <TimePicker
        name={name + 'Time'}
        placeholderText={t('entry.time_placeholder')}
        selected={dateAndTime.time}
        referenceDate={dateAndTime.date}
        timeIntervals={30}
        timeCaption={t('time')}
        onChange={i => this.timeChanged(i)}
        isDisabled={!dateAndTime.date || isDisabled}
        isClearable={isClearable}
        dateFormat="HH:mm"
        timeFormat="HH:mm"
      />
    );

    return (
      <DateTimeContainer>
        <InputLabel htmlFor={name + 'Date'}>{title}</InputLabel>
        <DatePicker
          name={name + 'Date'}
          placeholderText={datePlaceholder || t('entry.date_placeholder')}
          selected={dateAndTime.date}
          isDisabled={isDisabled}
          isClearable={isClearable}
          onChange={i => this.dateChanged(i)}
        />
        {this.props.showTime ? timePicker : null}
      </DateTimeContainer>
    );
  }

  private dateChanged(value: Date) {
    const { showTime } = this.props;

    if (!value) {
      this.props.changed(null, false);
      return;
    }

    // value is a JS Date in local timezone from the date picker
    // Extract the local date components (what the user selected)
    const year = getYear(value);
    const month = getMonth(value);
    const day = getDate(value);

    if (showTime) {
      const currentDateTime = this.getDateAndTimeFromProps();

      if (currentDateTime.time) {
        // Keep current time (what user sees), just change date
        const hours = getHours(currentDateTime.time);
        const minutes = getMinutes(currentDateTime.time);

        // Create a new local date with the new date + existing time
        const localDateTime = new Date(year, month, day, hours, minutes, 0, 0);
        // Convert to UTC for storage
        const utcDateTime = fromZonedTime(localDateTime, localTimeZone);

        this.props.changed(utcDateTime.toISOString(), true);
      } else {
        // No previous time, default to 00:00 local time
        const localDateTime = new Date(year, month, day, 0, 0, 0, 0);
        const utcDateTime = fromZonedTime(localDateTime, localTimeZone);

        this.props.changed(utcDateTime.toISOString(), true);
      }
    } else {
      // Date only, no time - store as midnight UTC on that date
      const utcDate = new Date(Date.UTC(year, month, day, 0, 0, 0, 0));
      this.props.changed(utcDate.toISOString(), false);
    }
  }

  private timeChanged(value: Date) {
    if (!value) {
      const previous = this.getDateAndTimeFromProps();

      if (previous.date) {
        // Time cleared, keep date only at 00:00 local time
        const year = getYear(previous.date);
        const month = getMonth(previous.date);
        const day = getDate(previous.date);

        const localDateTime = new Date(year, month, day, 0, 0, 0, 0);
        const utcDateTime = fromZonedTime(localDateTime, localTimeZone);

        this.props.changed(utcDateTime.toISOString(), false);
      } else {
        this.props.changed(null, false);
      }
      return;
    }

    const previous = this.getDateAndTimeFromProps();
    if (!previous.date) return;

    // Get the date from the previous value (what user sees in the date picker)
    const year = getYear(previous.date);
    const month = getMonth(previous.date);
    const day = getDate(previous.date);

    // Get the time from the new value (what user selected/typed)
    const hours = getHours(value);
    const minutes = getMinutes(value);

    // Combine date + time in local timezone
    const localDateTime = new Date(year, month, day, hours, minutes, 0, 0);
    // Convert to UTC for storage
    const utcDateTime = fromZonedTime(localDateTime, localTimeZone);

    this.props.changed(utcDateTime.toISOString(), true);
  }

  private getDateAndTimeFromProps(): { date: Date | null; time: Date | null; hasTime: boolean } {
    const { dateTime, hasTime } = this.props;

    if (!dateTime) {
      return { date: null, time: null, hasTime };
    }

    // Parse the ISO string (stored in UTC)
    const utcDate = parseISO(dateTime);

    if (!isValid(utcDate)) {
      console.warn('Invalid DateTime:', dateTime);
      return { date: null, time: null, hasTime };
    }

    // Convert UTC to local timezone for display
    const localDate = toZonedTime(utcDate, localTimeZone);

    return {
      date: localDate,
      time: hasTime ? localDate : null,
      hasTime
    };
  }
}

export default withTranslation()(DateTimePicker) as React.ComponentType<ComponentProps>;
